import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import {
  ensureProspect,
  getSessionAndSale,
  saveLeadNote,
} from "./dealsCrmActions";
import { enrichLeadFields, fillEmptyPatch } from "./leadEnrich";
import {
  buildDetailRows,
  fallbackRows,
  presentRows,
  prospectSections,
  type DetailRow,
} from "./leadDetailRows";
import { leadOwner } from "./leadUtils";
import { useLeadDetail } from "./useLeadDetail";
import type { DealLead } from "./types";

interface LeadDetailModalProps {
  item: DealLead | null;
  visibleLeads: DealLead[]; // filtered + sorted list, for Prev/Next paging
  onNavigate: (lead: DealLead) => void;
  onClose: () => void;
}

const RowList = ({ rows }: { rows: DetailRow[] }) => (
  <div className="space-y-1">
    {presentRows(rows).map(([label, value]) => (
      <div key={label} className="flex gap-3 text-sm">
        <span className="w-40 shrink-0 text-muted-foreground">{label}</span>
        <span className="min-w-0 break-words">{String(value)}</span>
      </div>
    ))}
  </div>
);

const SectionHeading = ({ children }: { children: string }) => (
  <div className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
    {children}
  </div>
);

/** Contact Notes textarea persisted to the lead's Airtable record on blur. */
const NotesField = ({ item }: { item: DealLead }) => {
  const notify = useNotify();
  const [value, setValue] = useState(item.notes ?? "");
  const [saved, setSaved] = useState<string>(item.notes ?? "");
  useEffect(() => {
    setValue(item.notes ?? "");
    setSaved(item.notes ?? "");
  }, [item]);
  return (
    <div className="mt-4">
      <SectionHeading>Contact notes</SectionHeading>
      <Textarea
        value={value}
        rows={3}
        placeholder="Notes saved back to Airtable…"
        onChange={(e) => setValue(e.target.value)}
        onBlur={async () => {
          if (value === saved) return;
          try {
            await saveLeadNote(item, value);
            setSaved(value);
            // Keep the in-memory lead current for re-opens this session.
            (item as { notes: string | null }).notes = value;
            notify("✓ Note saved");
          } catch (e) {
            console.error("save note failed", e);
            notify("Could not save note", { type: "warning" });
          }
        }}
      />
    </div>
  );
};

export const LeadDetailModal = ({
  item,
  visibleLeads,
  onNavigate,
  onClose,
}: LeadDetailModalProps) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { data, isPending, refetch } = useLeadDetail(item);
  const [busy, setBusy] = useState(false);

  if (!item) return null;
  const isAuction = item.kind === "auction";
  const idx = visibleLeads.findIndex(
    (x) => x.kind === item.kind && x.id === item.id,
  );
  const prospect = data?.prospect ?? null;
  const fields = data?.airtableFields ?? null;

  const title =
    (prospect
      ? prospect.owner_name
      : fields
        ? fields["Borrower / Owner"] || fields["Decedent"]
        : leadOwner(item)) || "Lead";

  const refreshFromSource = async () => {
    if (!prospect) return;
    setBusy(true);
    try {
      const ctx = await getSessionAndSale();
      if (!ctx) return;
      const patch = fillEmptyPatch(await enrichLeadFields(item), prospect);
      if (!Object.keys(patch).length) {
        notify("No new data from source", { type: "warning" });
        return;
      }
      const { error } = await ctx.sb
        .from("prospects")
        .update(patch)
        .eq("id", prospect.id);
      notify(error ? "Refresh failed" : "✓ Prospect refreshed", {
        type: error ? "warning" : "info",
      });
      if (!error) refetch();
    } finally {
      setBusy(false);
    }
  };

  const openCreating = async () => {
    setBusy(true);
    try {
      const ctx = await getSessionAndSale();
      if (!ctx) {
        notify("Sign in first", { type: "warning" });
        return;
      }
      const pid = await ensureProspect(ctx, item);
      if (pid) {
        onClose();
        navigate(`/prospects/${pid}/show`);
      } else {
        notify("Could not open", { type: "warning" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="pr-6">{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {isPending ? (
            <div className="py-10 text-center text-muted-foreground">
              Loading…
            </div>
          ) : prospect ? (
            <>
              <div className="text-xs font-semibold text-primary">
                ✓ In Prospects · stage: {prospect.stage ?? ""}
              </div>
              {prospectSections(prospect).map(([heading, rows]) => {
                const present = presentRows(rows);
                if (!present.length) return null;
                return (
                  <div key={heading}>
                    <SectionHeading>{heading}</SectionHeading>
                    <RowList rows={present} />
                  </div>
                );
              })}
              {data && data.activities.length > 0 && (
                <>
                  <SectionHeading>Activities</SectionHeading>
                  <RowList
                    rows={data.activities.map((a) => [
                      `${(a.type ?? "").replace(/_/g, " ")}${a.completed_at ? " ✓" : ""}`,
                      `${a.title ?? ""}${a.starts_at ? ` · ${String(a.starts_at).slice(0, 10)}` : ""}`,
                    ])}
                  />
                </>
              )}
              {data && data.notes.length > 0 && (
                <>
                  <SectionHeading>Notes</SectionHeading>
                  <div className="space-y-2">
                    {data.notes.map((n, i) => (
                      <div
                        key={i}
                        className="border-b pb-2 text-sm last:border-0"
                      >
                        <div>{n.body}</div>
                        <div className="text-xs text-muted-foreground">
                          {String(n.created_at).slice(0, 10)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-xs text-muted-foreground">
                {isAuction ? "🏛️ Auction" : "⚖️ Probate"} lead — not yet a
                prospect
              </div>
              <div className="mt-2">
                <RowList
                  rows={
                    fields
                      ? buildDetailRows(fields, isAuction)
                      : fallbackRows(item)
                  }
                />
              </div>
              <NotesField item={item} />
            </>
          )}
        </div>
        <div className="flex gap-2 border-t pt-3">
          {prospect ? (
            <>
              <Button
                className="flex-1"
                variant="outline"
                disabled={busy}
                onClick={refreshFromSource}
              >
                ↻ Refresh from source
              </Button>
              <Button
                className="flex-1"
                disabled={busy}
                onClick={() => {
                  onClose();
                  navigate(`/prospects/${prospect.id}/show`);
                }}
              >
                Open
              </Button>
            </>
          ) : (
            <Button
              className="flex-1"
              disabled={busy || isPending}
              onClick={openCreating}
            >
              {busy ? "Opening…" : "Open"}
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={idx <= 0}
            onClick={() => onNavigate(visibleLeads[idx - 1])}
          >
            ‹ Prev
          </Button>
          <span className="text-xs text-muted-foreground">
            {idx >= 0 ? `${idx + 1} of ${visibleLeads.length}` : ""}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={idx < 0 || idx >= visibleLeads.length - 1}
            onClick={() => onNavigate(visibleLeads[idx + 1])}
          >
            Next ›
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
